import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { CustomFilterV2, EmptyState, StatCardV2 } from '../../../components/v2';
import { canViewConformidadeInspecao } from '../../../helpers/conformidadeInspecaoPermission';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import TableService from '../../../services/TableService';
import CalendarioGeralDialog from './CalendarioGeralDialog';
import CalendarioPessoaDialog from './CalendarioPessoaDialog';
import ResumoInspetorDialog from './ResumoInspetorDialog';
import ResumoTable from './ResumoTable';
import {
  CICLO_CUSTOM,
  CICLOS,
  applyCicloToFilter,
  buildApiBody,
  buildSearchFromView,
  REGIME_TABS,
  calendarioEmpresaFromFilterRows,
  countDiasCiclo,
  filterPessoasPorRegime,
  formatPeriodLabel,
  getDefaultView,
  isPeriodInvalid,
  normalizeResumo,
  parseViewFromSearch,
  viewsEqual,
} from './constants';

const RelatorioConformidadeInspecaoV2 = ({
  history,
  location,
  authData,
  moduleConfigurations,
}) => {
  const canView = canViewConformidadeInspecao({ authData, moduleConfigurations });
  const [view, setView] = useState(() => parseViewFromSearch(location.search, getDefaultView()));
  const [openFilter, setOpenFilter] = useState(false);
  const [filterError, setFilterError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [report, setReport] = useState(() => normalizeResumo({}));
  const [resumo, setResumo] = useState(null);
  const [regimeRows, setRegimeRows] = useState(null);
  const [regimeLoading, setRegimeLoading] = useState(false);
  const [regimeError, setRegimeError] = useState(false);
  const autoResumoInspetorId = useRef(null);

  const syncUrl = useCallback((next) => {
    const search = buildSearchFromView(next);
    const current = (location.search || '').replace(/^\?/, '');
    if (search === current) return;
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    const fromUrl = parseViewFromSearch(location.search, getDefaultView());
    setView((current) => (viewsEqual(current, fromUrl) ? current : fromUrl));
  }, [location.search]);

  useEffect(() => {
    syncUrl(view);
  }, [view, syncUrl]);

  const updateView = (patch) => {
    const next = { ...view, ...patch };
    setView(next);
    syncUrl(next);
  };

  const periodInvalid = isPeriodInvalid(view.inicio, view.fim);
  const regime = view.regime || 'GERAL';
  const scopedInspetorId = view.inspetorId ? String(view.inspetorId) : null;
  const openCalendarioGeral = view.visao === 'calendario' && !scopedInspetorId;

  const fetchReport = useCallback(async () => {
    if (!canView || periodInvalid) return;
    setLoading(true);
    setFetchError(false);
    try {
      const response = await TableService.request(
        'reports/conformidade-inspecao/resumo',
        buildApiBody(view.inicio, view.fim, {
          usuarioIds: scopedInspetorId ? [scopedInspetorId] : null,
        }),
      );
      setReport(normalizeResumo(response?.data));
    } catch {
      setReport(normalizeResumo({}));
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [canView, periodInvalid, scopedInspetorId, view.fim, view.inicio]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    setRegimeRows(null);
    setRegimeError(false);
  }, [scopedInspetorId, view.inicio, view.fim]);

  useEffect(() => {
    if (regime === 'GERAL' || !canView || periodInvalid || regimeRows) return undefined;
    let cancelled = false;
    const run = async () => {
      setRegimeLoading(true);
      setRegimeError(false);
      try {
        const all = [];
        let page = 0;
        let total = Infinity;
        while (all.length < total && page < 20) {
          const response = await TableService.request(
            `reports/conformidade-inspecao/filter?p=${page}&s=50`,
            buildApiBody(view.inicio, view.fim, {
              page,
              size: 50,
              usuarioIds: scopedInspetorId ? [scopedInspetorId] : null,
            }),
          );
          const rows = response?.data?.rows || [];
          total = Number(response?.data?.totalRows) || rows.length;
          all.push(...rows);
          if (!rows.length) break;
          page += 1;
        }
        if (!cancelled) setRegimeRows(all);
      } catch {
        if (!cancelled) {
          setRegimeRows([]);
          setRegimeError(true);
        }
      } finally {
        if (!cancelled) setRegimeLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [canView, periodInvalid, regime, regimeRows, scopedInspetorId, view.fim, view.inicio]);

  const pessoasCiclo = useMemo(
    () => filterPessoasPorRegime(report.pessoas, regime),
    [regime, report.pessoas],
  );
  const calendario = useMemo(() => {
    if (regime === 'GERAL') return report.calendario;
    if (!regimeRows) return [];
    return calendarioEmpresaFromFilterRows(regimeRows, regime);
  }, [regime, regimeRows, report.calendario]);
  const regimeCalendarioLoading = regime !== 'GERAL' && regimeRows == null;

  const pessoaEscopo = useMemo(() => {
    if (!scopedInspetorId) return null;
    const found = (report.pessoas || []).find(
      (pessoa) => String(pessoa.usuarioId) === scopedInspetorId,
    );
    if (found) return found;
    return {
      usuarioId: scopedInspetorId,
      usuarioNome: 'Inspetor',
      regime: '—',
      diasComOcorrencia: 0,
      classificacoes: [],
    };
  }, [report.pessoas, scopedInspetorId]);

  const calendarioPessoa = useMemo(() => {
    if (view.visao !== 'calendario' || !scopedInspetorId) return null;
    return pessoaEscopo;
  }, [pessoaEscopo, scopedInspetorId, view.visao]);

  useEffect(() => {
    if (!scopedInspetorId) {
      autoResumoInspetorId.current = null;
      return;
    }
    if (view.visao !== 'resumo' || loading) return;
    if (autoResumoInspetorId.current === scopedInspetorId) return;
    autoResumoInspetorId.current = scopedInspetorId;
    setResumo({
      pessoa: pessoaEscopo,
      codigo: null,
      severidade: null,
    });
  }, [loading, pessoaEscopo, scopedInspetorId, view.visao]);

  const clearEscopoInspetor = () => {
    autoResumoInspetorId.current = null;
    setResumo(null);
    updateView({ inspetorId: null, visao: null });
  };

  const pessoasAlta = useMemo(() => {
    if (report.semSeveridade) return null;
    return pessoasCiclo.filter((pessoa) => pessoa.piorSeveridade === 'ALTA').length;
  }, [pessoasCiclo, report.semSeveridade]);

  const diasComOcorrencia = (regime === 'GERAL' && report.semSerie) || regimeCalendarioLoading
    ? null
    : calendario.length;
  const diasQuietos = (regime === 'GERAL' && report.semSerie) || regimeCalendarioLoading
    ? null
    : Math.max(0, countDiasCiclo(view.inicio, view.fim) - calendario.length);

  const openResumo = (pessoa, parte) => {
    setResumo({
      pessoa,
      codigo: parte?.codigo || null,
      severidade: parte?.severidade || null,
    });
  };

  const openCalendarioPessoa = (pessoa) => {
    if (!pessoa?.usuarioId) return;
    setResumo(null);
    updateView({
      visao: 'calendario',
      inspetorId: String(pessoa.usuarioId),
    });
  };

  const closeCalendario = () => updateView({ visao: null });

  const closeResumo = () => {
    setResumo(null);
    if (view.visao === 'resumo') updateView({ visao: null });
  };

  const onApplyFilter = (filterData) => {
    const next = applyCicloToFilter(filterData);
    if (next.ciclo === CICLO_CUSTOM && isPeriodInvalid(next.inicio, next.fim)) {
      setFilterError('A data de início deve ser anterior ou igual à data de fim.');
      return;
    }
    setFilterError(null);
    setOpenFilter(false);
    autoResumoInspetorId.current = null;
    setResumo(null);
    updateView({
      ciclo: next.ciclo,
      inicio: next.inicio,
      fim: next.fim,
      visao: null,
      inspetorId: null,
    });
  };

  if (!canView) {
    return (
      <Box>
        <EmptyState
          title="Acesso restrito"
          message="A conformidade de inspeção não está disponível para o seu usuário. Solicite acesso ao administrador."
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        {(loading || regimeLoading) && <LinearProgress />}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4">Conformidade de inspeção</Typography>
            <Typography variant="body2" color="text.secondary">
              {`Ciclo ${formatPeriodLabel(view.inicio, view.fim)}`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<CalendarMonthOutlinedIcon />}
              onClick={() => {
                if (scopedInspetorId && pessoaEscopo) {
                  openCalendarioPessoa(pessoaEscopo);
                  return;
                }
                updateView({ visao: 'calendario', inspetorId: null });
              }}
            >
              Ver no calendário
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<FilterListOutlinedIcon />}
              onClick={() => setOpenFilter(true)}
            >
              Filtros
            </Button>
          </Stack>
        </Stack>

        {scopedInspetorId && (
          <Alert
            severity="info"
            action={(
              <Button color="inherit" size="small" onClick={clearEscopoInspetor}>
                Ver ciclo completo
              </Button>
            )}
          >
            {`Filtrado em ${pessoaEscopo?.usuarioNome || 'inspetor'}. O relatório busca só esta pessoa.`}
          </Alert>
        )}

        <Tabs
          value={regime}
          onChange={(_, next) => updateView({ regime: next })}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {REGIME_TABS.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={tab.label}
              sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }}
            />
          ))}
        </Tabs>

        {periodInvalid && (
          <Alert severity="warning">A data de início deve ser anterior ou igual à data de fim.</Alert>
        )}
        {fetchError && (
          <Alert severity="error">
            Não foi possível carregar o relatório. Confira se o endpoint de conformidade de inspeção já está no ar.
          </Alert>
        )}
        {!fetchError && report.semSerie && report.linhas.length > 0 && (
          <Alert severity="info">
            O resumo ainda não traz a série do ciclo. O calendário mostra as datas; os chips entram quando o servidor enviar os problemas do dia.
          </Alert>
        )}
        {!fetchError && report.semSeveridade && (
          <Alert severity="info">
            A severidade ainda não vem separada no resumo. Dias de “convocado sem ponto” com e sem campo podem estar na mesma linha.
          </Alert>
        )}
        {(pessoasCiclo || []).some((pessoa) => pessoa.estimado) && (
          <Alert severity="warning">
            Alguma pessoa está sem cidade ou estado para feriado municipal. O calendário dela considera só feriado nacional e estadual.
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <StatCardV2 label="Pessoas em severidade alta" value={pessoasAlta} color="error" loading={loading} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <StatCardV2
              label="Dias com ocorrência"
              value={diasComOcorrencia}
              color="warning"
              loading={loading || regimeCalendarioLoading}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <StatCardV2
              label="Dias do ciclo sem ocorrência"
              value={diasQuietos}
              color="success"
              loading={loading || regimeCalendarioLoading}
            />
          </Box>
        </Stack>

        <ResumoTable
          pessoas={pessoasCiclo}
          onOpenResumo={openResumo}
          onOpenCalendario={openCalendarioPessoa}
        />

        <CalendarioGeralDialog
          open={openCalendarioGeral}
          inicio={view.inicio}
          fim={view.fim}
          dias={calendario}
          regime={regime}
          loading={loading || regimeCalendarioLoading}
          loadError={regimeError}
          onClose={closeCalendario}
          onOpenResumo={openResumo}
          onOpenCalendario={openCalendarioPessoa}
        />
        <ResumoInspetorDialog
          pessoa={resumo?.pessoa}
          codigo={resumo?.codigo}
          severidade={resumo?.severidade}
          inicio={view.inicio}
          fim={view.fim}
          onClose={closeResumo}
          onOpenCalendario={(pessoa) => {
            setResumo(null);
            openCalendarioPessoa(pessoa);
          }}
        />
        <CalendarioPessoaDialog
          pessoa={calendarioPessoa}
          inicio={view.inicio}
          fim={view.fim}
          onClose={closeCalendario}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => {
            setOpenFilter(false);
            setFilterError(null);
          }}
          title="Filtros"
          fullWidth
          defaultFilter={getDefaultView()}
          appliedFilter={view}
          confirmButtonText="Aplicar"
          fields={[
            {
              field: 'useSelectOption',
              key: 'ciclo',
              label: 'Ciclo',
              options: CICLOS,
              md: 12,
              xs: 12,
              showEmpty: false,
              apply: applyCicloToFilter,
            },
            {
              field: 'useDates',
              md: 6,
              disabled: (filter) => filter.ciclo !== CICLO_CUSTOM,
            },
          ]}
          onFilter={onApplyFilter}
        />
        {filterError && (
          <Alert severity="warning" onClose={() => setFilterError(null)}>
            {filterError}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

RelatorioConformidadeInspecaoV2.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  authData: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

export default withRouter(connect(mapStateToProps)(RelatorioConformidadeInspecaoV2));
